import { useEffect, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { AppDispatch } from '@/shared/store/store';
import { useGlobalNotifications } from '@/shared/contexts/NotificationContext';

/**
 * Hook para escuchar eventos WebSocket globalmente
 * Maneja eventos de cotizaciones y otros eventos en tiempo real
 */
export const useWebSocketEvents = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { showSuccess, showError } = useGlobalNotifications();

  // Handler para nuevas cotizaciones
  const handleNewQuoteForReview = useCallback((event: CustomEvent) => {

    // Mostrar notificación toast
    showSuccess(
      '🎉 Nueva Cotización',
      `${event.detail.agentName} ha enviado una nueva cotización para revisión`
    );

    // Emitir evento personalizado para que los componentes específicos lo capturen
    window.dispatchEvent(new CustomEvent('quote:new-for-review:ui', {
      detail: event.detail
    }));
  }, [showSuccess]);

  // Handler para cotizaciones enviadas
  const handleQuoteSent = useCallback((event: CustomEvent) => {
    
    showSuccess(
      '📤 Cotización Enviada',
      'Tu cotización ha sido enviada exitosamente'
    );

    window.dispatchEvent(new CustomEvent('quote:sent:ui', {
      detail: event.detail
    }));
  }, [showSuccess]);

  // Handler para cotizaciones aceptadas
  const handleQuoteAccepted = useCallback((event: CustomEvent) => {
    
    showSuccess(
      '✅ Cotización Aceptada',
      'Tu cotización ha sido aceptada por la agencia'
    );

    window.dispatchEvent(new CustomEvent('quote:accepted:ui', {
      detail: event.detail
    }));
  }, [showSuccess]);

  // Handler para cotizaciones rechazadas
  const handleQuoteRejected = useCallback((event: CustomEvent) => {
    
    showError(
      '❌ Cotización Rechazada',
      event.detail.reason || 'Tu cotización ha sido rechazada por la agencia'
    );

    window.dispatchEvent(new CustomEvent('quote:rejected:ui', {
      detail: event.detail
    }));
  }, [showError]);

  // Handler para cotizaciones canceladas
  const handleQuoteCancelled = useCallback((event: CustomEvent) => {
    
    showError(
      '🚫 Cotización Cancelada',
      'La cotización ha sido cancelada'
    );

    window.dispatchEvent(new CustomEvent('quote:cancelled:ui', {
      detail: event.detail
    }));
  }, [showError]);

  // Configurar listeners globales
  useEffect(() => {

    // Agregar listeners
    window.addEventListener('quote:new-for-review', handleNewQuoteForReview as EventListener);
    window.addEventListener('quote:sent', handleQuoteSent as EventListener);
    window.addEventListener('quote:accepted', handleQuoteAccepted as EventListener);
    window.addEventListener('quote:rejected', handleQuoteRejected as EventListener);
    window.addEventListener('quote:cancelled', handleQuoteCancelled as EventListener);


    // Limpiar listeners al desmontar
    return () => {
      window.removeEventListener('quote:new-for-review', handleNewQuoteForReview as EventListener);
      window.removeEventListener('quote:sent', handleQuoteSent as EventListener);
      window.removeEventListener('quote:accepted', handleQuoteAccepted as EventListener);
      window.removeEventListener('quote:rejected', handleQuoteRejected as EventListener);
      window.removeEventListener('quote:cancelled', handleQuoteCancelled as EventListener);
    };
  }, [
    handleNewQuoteForReview,
    handleQuoteSent,
    handleQuoteAccepted,
    handleQuoteRejected,
    handleQuoteCancelled
  ]);

  return {
    // Exponer funciones para uso manual si es necesario
    handleNewQuoteForReview,
    handleQuoteSent,
    handleQuoteAccepted,
    handleQuoteRejected,
    handleQuoteCancelled,
  };
};
